import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Download, Mail, Link as LinkIcon, Check, X, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCreateShare, downloadVCard } from '@/hooks/use-sharing'
import type { Contact } from '@/lib/types'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'

interface ShareContactDialogProps {
  contact: Contact
  onClose: () => void
}

const FIELD_OPTIONS = [
  { key: 'email', labelKey: 'sharing.fields.email', defaultLabel: 'Email' },
  { key: 'phone', labelKey: 'sharing.fields.phone', defaultLabel: 'Phone' },
  { key: 'company', labelKey: 'sharing.fields.company', defaultLabel: 'Company' },
  { key: 'jobTitle', labelKey: 'sharing.fields.jobTitle', defaultLabel: 'Job Title' },
  { key: 'location', labelKey: 'sharing.fields.location', defaultLabel: 'Location' },
  { key: 'birthday', labelKey: 'sharing.fields.birthday', defaultLabel: 'Birthday' },
  { key: 'anniversary', labelKey: 'sharing.fields.anniversary', defaultLabel: 'Anniversary' },
  { key: 'nickname', labelKey: 'sharing.fields.nickname', defaultLabel: 'Nickname' },
  { key: 'notes', labelKey: 'sharing.fields.notes', defaultLabel: 'Notes' },
  { key: 'links', labelKey: 'sharing.fields.links', defaultLabel: 'Social Links' },
] as const

export function ShareContactDialog({ contact, onClose }: ShareContactDialogProps) {
  const { t } = useTranslation()
  const createShare = useCreateShare()
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(['email', 'phone', 'company', 'jobTitle']),
  )
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [expiresInDays, setExpiresInDays] = useState<number | undefined>(7)

  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  const toggleField = (field: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev)
      if (next.has(field)) {
        next.delete(field)
      } else {
        next.add(field)
      }
      return next
    })
  }

  const handleCreateLink = async () => {
    const result = await createShare.mutateAsync({
      contactId: contact.id,
      includedFields: ['name', ...selectedFields],
      expiresInDays,
    })
    setShareUrl(result.shareUrl)
  }

  const handleCopyLink = async () => {
    if (!shareUrl) return
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    toast.success(t('sharing.copied', 'Link copied to clipboard'))
    setTimeout(() => setCopied(false), 2000)
  }

  const handleEmailShare = () => {
    const subject = encodeURIComponent(
      t('sharing.emailSubject', { name: fullName, defaultValue: `Contact: ${fullName}` }),
    )
    if (shareUrl) {
      const body = encodeURIComponent(
        t('sharing.emailBody', {
          name: fullName,
          url: shareUrl,
          defaultValue: `Here's the contact card for ${fullName}:\n\n${shareUrl}`,
        }),
      )
      window.open(`mailto:?subject=${subject}&body=${body}`, '_self')
    } else {
      // Share basic info via email without link
      const parts = [fullName]
      if (selectedFields.has('email') && contact.email) parts.push(`Email: ${contact.email}`)
      if (selectedFields.has('phone') && contact.phone) parts.push(`Phone: ${contact.phone}`)
      if (selectedFields.has('company') && contact.company)
        parts.push(`Company: ${contact.company}`)
      if (selectedFields.has('jobTitle') && contact.jobTitle)
        parts.push(`Title: ${contact.jobTitle}`)
      const body = encodeURIComponent(parts.join('\n'))
      window.open(`mailto:?subject=${subject}&body=${body}`, '_self')
    }
  }

  const handleDownloadVCard = () => {
    downloadVCard(contact.id)
  }

  // Check which fields the contact actually has data for
  const hasField = (key: string): boolean => {
    const val = contact[key as keyof Contact]
    return val !== null && val !== undefined && val !== ''
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div className="bg-surface-container-low max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl p-5 shadow-lg sm:rounded-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="text-primary size-5" />
            <h2 className="text-lg font-semibold">{t('sharing.title', 'Share Contact')}</h2>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        <p className="text-on-surface-variant mb-4 text-sm">
          {t('sharing.description', {
            name: fullName,
            defaultValue: `Choose which details to share for ${fullName}`,
          })}
        </p>

        {/* Privacy controls — field toggles */}
        <div className="mb-4">
          <h3 className="mb-2 text-sm font-medium">
            {t('sharing.includeFields', 'Include fields')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {FIELD_OPTIONS.map((field) => {
              const available = hasField(field.key)
              return (
                <button
                  key={field.key}
                  disabled={!available}
                  onClick={() => toggleField(field.key)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    !available && 'border-outline-variant/30 cursor-not-allowed opacity-30',
                    available && selectedFields.has(field.key)
                      ? 'bg-primary text-on-primary border-primary'
                      : 'bg-surface-container-high text-on-surface-variant border-outline-variant/40 hover:bg-surface-container-highest',
                  )}
                >
                  {t(field.labelKey, field.defaultLabel)}
                </button>
              )
            })}
          </div>
        </div>

        {/* Expiry */}
        <div className="mb-4">
          <h3 className="mb-2 text-sm font-medium">{t('sharing.expiry', 'Link expires in')}</h3>
          <div className="flex gap-2">
            {[
              { value: 1, label: t('sharing.1day', '1 day') },
              { value: 7, label: t('sharing.7days', '7 days') },
              { value: 30, label: t('sharing.30days', '30 days') },
              { value: undefined, label: t('sharing.never', 'Never') },
            ].map((opt) => (
              <button
                key={opt.value ?? 'never'}
                onClick={() => setExpiresInDays(opt.value)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  expiresInDays === opt.value
                    ? 'bg-primary text-on-primary border-primary'
                    : 'bg-surface-container-high text-on-surface-variant border-outline-variant/40 hover:bg-surface-container-highest',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Share URL result */}
        {shareUrl && (
          <div className="bg-surface-container-high mb-4 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <LinkIcon className="text-primary size-4 shrink-0" />
              <span className="text-on-surface-variant flex-1 truncate text-xs">{shareUrl}</span>
              <Button variant="ghost" size="icon-xs" onClick={handleCopyLink}>
                {copied ? (
                  <Check className="bg-chartreuse text-chartreuse-foreground size-3.5 rounded-full p-0.5" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {!shareUrl ? (
            <Button onClick={handleCreateLink} disabled={createShare.isPending}>
              <LinkIcon className="mr-1.5 size-4" />
              {createShare.isPending
                ? t('sharing.creating', 'Creating...')
                : t('sharing.createLink', 'Create Share Link')}
            </Button>
          ) : (
            <Button onClick={handleCopyLink}>
              {copied ? <Check className="mr-1.5 size-4" /> : <Copy className="mr-1.5 size-4" />}
              {copied ? t('sharing.copied', 'Copied!') : t('sharing.copyLink', 'Copy Link')}
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={handleEmailShare}>
              <Mail className="mr-1.5 size-4" />
              {t('sharing.email', 'Email')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleDownloadVCard}>
              <Download className="mr-1.5 size-4" />
              {t('sharing.downloadVCard', 'vCard')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
